import React, { useEffect, useRef, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Chip,
  Divider,
  useMediaQuery,
  useTheme,
  TextField,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  Avatar,
  Tooltip,
  alpha,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../redux/userSlice';
import { toggleTheme } from '../redux/themeSlice';
import AuthModal from './auth/AuthModal';
import BrandMark from './BrandMark';
import DiscussionNotificationBell from './DiscussionNotificationBell';
import ChatBell from './chat/ChatBell';
import proxy from '../api/axios';
import { loadProblems, searchProblemsFromCache } from '../api/queries';
import { formatProblemTitle, resolveProfileImage } from '../common/utils';
import { TOUR_START_EVENT } from './ProductTour';

const ACCENT = '#137274';
const ACCENT_PINK = '#f50057';

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const loggedInUser = useSelector((state) => state.user.userInfo);
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    dispatch(logout());
    handleClose();
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  useEffect(() => {
    setAnchorEl(null);
  }, [isLoggedIn]);

  const hasNeetAccess = loggedInUser?.hasNeetAccess || loggedInUser?.role === 'admin';

  const navItems = [
    { text: 'Practice Track', path: '/practice-track', highlight: true },
    { text: 'Problem Set', path: '/problems#problemset' },
    { text: 'Interview Prep', path: '/interview-prep', badge: 'New' },
    { text: 'Contests', path: '/contests' },
    ...(hasNeetAccess ? [{ text: 'NEET Tests', path: '/neet-tests' }] : []),
    { text: '1:1 Mentorship', path: '/mentorship' },
    { text: 'IDE', path: '/ide' },
  ];

  const scrollToNavHash = (path) => {
    const hash = String(path || '').split('#')[1];
    if (!hash) return;
    // Defer so route/hash updates can settle first
    window.setTimeout(() => {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 80);
  };

  const handleThemeChange = () => {
    dispatch(toggleTheme());
  };

  const debounceTimeout = useRef(null);

  useEffect(() => {
    loadProblems().catch(() => {});
  }, []);

  const handleSearchChange = async (_event, value) => {
    setSearchInput(value);
    if (!value || value.length < 2) {
      setSearchResults([]);
      return;
    }

    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    const cached = searchProblemsFromCache(value);
    if (cached) {
      setSearchResults(cached);
      setLoading(false);
      return;
    }

    setLoading(true);

    debounceTimeout.current = setTimeout(async () => {
      try {
        const local = searchProblemsFromCache(value);
        if (local) {
          setSearchResults(local);
          return;
        }
        const response = await proxy.get(`/problems/search?q=${value}`);
        setSearchResults(response.data.problems || []);
      } catch (error) {
        console.error('Error fetching search results:', error);
      } finally {
        setLoading(false);
      }
    }, 300);
  };

  const borderColor = isDark ? '#30363d' : 'rgba(15, 60, 70, 0.1)';
  const muted = isDark ? '#8b949e' : '#5f6b73';

  const brand = (
    <Box
      component={Link}
      to="/"
      data-tour="nav-brand"
      sx={{
        cursor: 'pointer',
        textDecoration: 'none',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.85,
        flexShrink: 0,
        mr: { xs: 1, md: 2 },
        '&:hover': { opacity: 0.92 },
      }}
    >
      <BrandMark size={32} variant="header" />
      <Typography
        component="span"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          m: 0,
          p: 0,
          height: 32,
          fontWeight: 800,
          fontSize: { xs: '1.15rem', sm: '1.3rem' },
          letterSpacing: '-0.03em',
          lineHeight: 1,
          color: isDark ? '#e6edf3' : '#0f2a2b',
        }}
      >
        Crafter
        <Box component="span" sx={{ color: ACCENT_PINK, ml: 0.35 }}>
          Hack
        </Box>
      </Typography>
    </Box>
  );

  const searchField = !isMobile && (
    <Autocomplete
      freeSolo
      size="small"
      options={searchResults}
      filterOptions={(x) => x}
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : formatProblemTitle(option.title) || ''
      }
      isOptionEqualToValue={(a, b) => a?.slug === b?.slug}
      loading={loading}
      inputValue={searchInput}
      onInputChange={handleSearchChange}
      onChange={(_e, option) => {
        if (option?.slug) {
          navigate(`/problems/${option.slug}`);
          setSearchInput('');
          setSearchResults([]);
        }
      }}
      noOptionsText={
        searchInput.trim().length < 2 ? (
          <Box sx={{ px: 0.5, py: 0.75 }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, mb: 0.25 }}>
              Search problems
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: muted }}>
              Type at least 2 characters to find a problem.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ px: 0.5, py: 0.75 }}>
            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, mb: 0.25 }}>
              No matches
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: muted }}>
              Try another keyword or browse the problem set.
            </Typography>
          </Box>
        )
      }
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            mt: 1,
            borderRadius: 2.5,
            border: `1px solid ${borderColor}`,
            bgcolor: isDark ? '#161b22' : '#ffffff',
            boxShadow: isDark
              ? '0 16px 40px rgba(0,0,0,0.45)'
              : '0 16px 40px rgba(15,60,70,0.14)',
            overflow: 'hidden',
            '& .MuiAutocomplete-listbox': {
              py: 0.75,
              maxHeight: 360,
            },
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search problems…"
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ ml: 0.25, mr: 0 }}>
                <SearchIcon sx={{ fontSize: 18, color: muted }} />
              </InputAdornment>
            ),
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress size={14} sx={{ color: ACCENT, mr: 0.5 }} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        const difficulty = String(option.difficulty || '').toLowerCase();
        const diffColor =
          difficulty === 'easy'
            ? '#1a7f37'
            : difficulty === 'medium'
              ? '#d97706'
              : difficulty === 'hard'
                ? '#cf222e'
                : muted;
        const topic = option.topics?.[0];

        return (
          <Box
            component="li"
            key={option.slug || key}
            {...rest}
            sx={{
              '&.MuiAutocomplete-option': {
                mx: 0.75,
                my: 0.25,
                px: 1.25,
                py: 1.1,
                borderRadius: 2,
                alignItems: 'flex-start',
                gap: 1.25,
              },
              '&.MuiAutocomplete-option[aria-selected="true"]': {
                bgcolor: isDark ? alpha(ACCENT, 0.18) : alpha(ACCENT, 0.1),
              },
              '&.MuiAutocomplete-option.Mui-focused': {
                bgcolor: isDark ? alpha('#fff', 0.06) : alpha(ACCENT, 0.07),
              },
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mt: 0.15,
                bgcolor: alpha(ACCENT, isDark ? 0.22 : 0.1),
                color: ACCENT,
              }}
            >
              <SearchIcon sx={{ fontSize: 16 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatProblemTitle(option.title)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.45, flexWrap: 'wrap' }}>
                {difficulty && (
                  <Chip
                    size="small"
                    label={difficulty.charAt(0).toUpperCase() + difficulty.slice(1)}
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      bgcolor: alpha(diffColor, isDark ? 0.2 : 0.1),
                      color: diffColor,
                      '& .MuiChip-label': { px: 0.75 },
                    }}
                  />
                )}
                {topic && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: muted,
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: 140,
                    }}
                  >
                    {String(topic)
                      .split(' ')
                      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                      .join(' ')}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        );
      }}
      sx={{
        width: { md: 200, lg: 280 },
        mr: 1.5,
        flexShrink: 0,
        transition: 'width 0.2s ease',
        '&:focus-within': {
          width: { md: 260, lg: 320 },
        },
        '& .MuiOutlinedInput-root': {
          borderRadius: 2.5,
          height: 36,
          bgcolor: isDark ? alpha('#fff', 0.04) : alpha(ACCENT, 0.05),
          fontSize: '0.85rem',
          fontWeight: 500,
          pr: '6px !important',
          transition: 'background 0.15s, box-shadow 0.15s',
          '& fieldset': {
            borderColor,
            transition: 'border-color 0.15s',
          },
          '&:hover': {
            bgcolor: isDark ? alpha('#fff', 0.06) : alpha(ACCENT, 0.07),
            '& fieldset': { borderColor: alpha(ACCENT, 0.4) },
          },
          '&.Mui-focused': {
            bgcolor: isDark ? '#161b22' : '#fff',
            boxShadow: `0 0 0 3px ${alpha(ACCENT, 0.18)}`,
            '& fieldset': { borderColor: `${ACCENT} !important`, borderWidth: '1px !important' },
          },
        },
        '& .MuiInputBase-input': {
          py: '7px !important',
          '&::placeholder': {
            color: muted,
            opacity: 1,
            fontWeight: 500,
          },
        },
        '& .MuiAutocomplete-endAdornment': {
          right: 6,
        },
      }}
    />
  );

  const navLinkSx = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    px: 1.1,
    py: 0.65,
    borderRadius: 1.5,
    textDecoration: 'none',
    fontSize: '0.8125rem',
    fontWeight: 550,
    color: isDark ? '#c9d1d9' : '#1a2b2c',
    whiteSpace: 'nowrap',
    transition: 'background 0.15s, color 0.15s',
    '&:hover': {
      bgcolor: isDark ? alpha('#fff', 0.06) : alpha(ACCENT, 0.07),
      color: isDark ? '#fff' : '#0f2a2b',
    },
    '&.active': {
      fontWeight: 700,
      color: ACCENT,
      bgcolor: isDark ? alpha(ACCENT, 0.18) : alpha(ACCENT, 0.1),
    },
  };

  const desktopNav = !isMobile && (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.25,
        flex: 1,
        minWidth: 0,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {navItems
        .map((item) => (
          <Box
            key={item.path}
            component={NavLink}
            to={item.path}
            end={item.path.startsWith('/problems')}
            onClick={() => scrollToNavHash(item.path)}
            sx={[
              navLinkSx,
              item.highlight && {
                color: ACCENT,
                bgcolor: isDark ? alpha(ACCENT, 0.12) : alpha(ACCENT, 0.08),
              },
            ]}
          >
            {item.text}
            {item.badge && (
              <Chip
                label={item.badge}
                size="small"
                sx={{
                  height: 16,
                  ml: 0.35,
                  fontSize: '0.6rem',
                  fontWeight: 800,
                  bgcolor: ACCENT_PINK,
                  color: '#fff',
                  '& .MuiChip-label': { px: 0.6 },
                }}
              />
            )}
          </Box>
        ))}
    </Box>
  );

  const accountMenu = (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      keepMounted
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        elevation: 0,
        sx: {
          mt: 1,
          minWidth: 200,
          borderRadius: 2,
          border: `1px solid ${borderColor}`,
          bgcolor: isDark ? '#161b22' : '#fff',
          boxShadow: isDark
            ? '0 12px 32px rgba(0,0,0,0.45)'
            : '0 12px 32px rgba(15,60,70,0.12)',
          '& .MuiMenuItem-root': {
            fontSize: '0.85rem',
            fontWeight: 550,
            borderRadius: 1,
            mx: 0.75,
            my: 0.25,
          },
        },
      }}
    >
      {(loggedInUser?.role === 'admin' || loggedInUser?.role === 'softadmin') && (
        <Box sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
          <Typography variant="caption" sx={{ color: muted, fontWeight: 700, letterSpacing: '0.06em' }}>
            ADMIN
          </Typography>
        </Box>
      )}
      {loggedInUser?.role === 'admin' && (
        <>
          <MenuItem component={Link} to="/admin/manage-problems" onClick={handleClose}>
            Manage Problems
          </MenuItem>
          <MenuItem component={Link} to="/admin/problem-feedback" onClick={handleClose}>
            Problem Feedback
          </MenuItem>
          <MenuItem component={Link} to="/admin/manage-contests" onClick={handleClose}>
            Manage Contests
          </MenuItem>
          <MenuItem component={Link} to="/admin/manage-neet-tests" onClick={handleClose}>
            Manage NEET Tests
          </MenuItem>
          <MenuItem component={Link} to="/admin/manage-users" onClick={handleClose}>
            Manage Users
          </MenuItem>
          <MenuItem component={Link} to="/admin/manage-careers" onClick={handleClose}>
            Career Applications
          </MenuItem>
          <MenuItem component={Link} to="/admin/broadcast-email" onClick={handleClose}>
            Broadcast Email
          </MenuItem>
          <MenuItem component={Link} to="/admin/analytics" onClick={handleClose}>
            Analytics
          </MenuItem>
          <Divider sx={{ my: 0.75 }} />
        </>
      )}
      {loggedInUser?.role === 'softadmin' && (
        <>
          <MenuItem component={Link} to="/admin/manage-problems" onClick={handleClose}>
            Manage Problems
          </MenuItem>
          <MenuItem component={Link} to="/admin/problem-feedback" onClick={handleClose}>
            Problem Feedback
          </MenuItem>
          <Divider sx={{ my: 0.75 }} />
        </>
      )}
      <MenuItem component={Link} to="/profile" onClick={handleClose}>
        My Profile
      </MenuItem>
      <MenuItem component={Link} to="/saved" onClick={handleClose}>
        Saved Problems
      </MenuItem>
      <MenuItem
        onClick={() => {
          handleClose();
          window.dispatchEvent(new CustomEvent('ch-start-tour'));
        }}
      >
        Take a quick tour
      </MenuItem>
      <MenuItem
        onClick={handleLogout}
        sx={{ color: `${ACCENT_PINK} !important`, fontWeight: '700 !important' }}
      >
        Log out
      </MenuItem>
    </Menu>
  );

  const rightActions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.25, sm: 0.75 }, flexShrink: 0, ml: 'auto' }}>
      {!isMobile && (
        <Tooltip title="Product tour">
          <IconButton
            size="small"
            onClick={() => window.dispatchEvent(new CustomEvent(TOUR_START_EVENT))}
            sx={{
              color: muted,
              '&:hover': { color: ACCENT, bgcolor: alpha(ACCENT, 0.08) },
            }}
          >
            <AutoAwesomeOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {!isMobile && (
        <Tooltip title="Live Coding Room">
          <IconButton
            component={Link}
            to="/live-coding"
            data-tour="nav-live"
            size="small"
            sx={{
              color: muted,
              '&:hover': { color: ACCENT_PINK, bgcolor: alpha(ACCENT_PINK, 0.08) },
            }}
          >
            <VideocamOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <DiscussionNotificationBell isLoggedIn={isLoggedIn} />
      <ChatBell isLoggedIn={isLoggedIn} />

      <Tooltip title={isDark ? 'Light mode' : 'Dark mode'}>
        <IconButton
          onClick={handleThemeChange}
          data-tour="theme-toggle"
          size="small"
          sx={{
            color: muted,
            '&:hover': { color: isDark ? '#fff' : '#0f2a2b', bgcolor: isDark ? alpha('#fff', 0.06) : alpha('#000', 0.04) },
          }}
        >
          {isDark ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
        </IconButton>
      </Tooltip>

      {!isMobile &&
        (isLoggedIn ? (
          <>
            <Button
              onClick={handleMenu}
              size="small"
              startIcon={
                <Avatar
                  src={resolveProfileImage(loggedInUser?.profileImage)}
                  sx={{
                    width: 26,
                    height: 26,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    bgcolor: alpha(ACCENT, 0.18),
                    color: ACCENT,
                  }}
                >
                  {(loggedInUser?.name?.[0] || 'U').toUpperCase()}
                </Avatar>
              }
              sx={{
                textTransform: 'none',
                fontWeight: 650,
                fontSize: '0.8125rem',
                color: isDark ? '#e6edf3' : '#0f2a2b',
                pl: 0.5,
                pr: 1,
                borderRadius: 2,
                '&:hover': { bgcolor: isDark ? alpha('#fff', 0.06) : alpha(ACCENT, 0.06) },
              }}
            >
              {loggedInUser?.name?.split?.(' ')?.[0] || 'User'}
            </Button>
            {accountMenu}
          </>
        ) : (
          <Button
            variant="contained"
            size="small"
            onClick={() => setAuthModalOpen(true)}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              px: 1.75,
              borderRadius: 2,
              bgcolor: ACCENT,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#0f5c5e', boxShadow: 'none' },
            }}
          >
            Sign in
          </Button>
        ))}

      {isMobile && (
        <IconButton
          onClick={toggleDrawer(true)}
          size="small"
          sx={{ color: isDark ? '#e6edf3' : '#0f2a2b' }}
          aria-label="Open menu"
        >
          <MenuIcon />
        </IconButton>
      )}
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: isDark ? alpha('#0f1419', 0.88) : alpha('#ffffff', 0.9),
          color: isDark ? '#e6edf3' : '#0f2a2b',
          borderBottom: `1px solid ${borderColor}`,
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
      >
        <Toolbar
          sx={{
            minHeight: { xs: 56, sm: 58 },
            px: { xs: 1.5, sm: 2.5 },
            gap: 0.5,
          }}
        >
          {brand}
          {desktopNav}
          {searchField}
          {rightActions}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            width: 300,
            bgcolor: isDark ? '#0f1419' : '#fff',
            borderLeft: `1px solid ${borderColor}`,
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${borderColor}`,
          }}
        >
          <Typography sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
            Menu
          </Typography>
          <IconButton size="small" onClick={toggleDrawer(false)} aria-label="Close menu">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <List sx={{ px: 1, pt: 1 }}>
          {navItems.map((item) => (
            <ListItemButton
              key={item.path}
              component={NavLink}
              to={item.path}
              end={item.path.startsWith('/problems')}
              onClick={() => {
                scrollToNavHash(item.path);
                toggleDrawer(false)();
              }}
              sx={{
                borderRadius: 1.5,
                mb: 0.35,
                ...(item.highlight && {
                  bgcolor: alpha(ACCENT, isDark ? 0.12 : 0.08),
                }),
                '&.active': {
                  bgcolor: alpha(ACCENT, isDark ? 0.18 : 0.1),
                  color: ACCENT,
                  '& .MuiListItemText-primary': { fontWeight: 700 },
                },
              }}
            >
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: 550,
                  sx: item.highlight ? { color: ACCENT } : undefined,
                }}
              />
              {item.badge && (
                <Chip
                  label={item.badge}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    fontWeight: 800,
                    bgcolor: ACCENT_PINK,
                    color: '#fff',
                  }}
                />
              )}
            </ListItemButton>
          ))}

          <ListItemButton
            component={NavLink}
            to="/live-coding"
            onClick={toggleDrawer(false)}
            sx={{ borderRadius: 1.5, mb: 0.35 }}
          >
            <VideocamOutlinedIcon sx={{ fontSize: 18, mr: 1.25, color: ACCENT_PINK }} />
            <ListItemText
              primary="Live Coding Room"
              primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 550 }}
            />
          </ListItemButton>
        </List>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ px: 2, pb: 2 }}>
          {isLoggedIn ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5 }}>
                <Avatar
                  src={resolveProfileImage(loggedInUser?.profileImage)}
                  sx={{
                    width: 36,
                    height: 36,
                    fontWeight: 700,
                    bgcolor: alpha(ACCENT, 0.18),
                    color: ACCENT,
                  }}
                >
                  {(loggedInUser?.name?.[0] || 'U').toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }} noWrap>
                    {loggedInUser?.name || 'User'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: muted }} noWrap>
                    {loggedInUser?.email || loggedInUser?.username || ''}
                  </Typography>
                </Box>
              </Box>
              {(loggedInUser?.role === 'admin' || loggedInUser?.role === 'softadmin') && (
                <>
                  {loggedInUser?.role === 'admin' && (
                    <>
                      <Button
                        fullWidth
                        component={Link}
                        to="/admin/manage-problems"
                        onClick={toggleDrawer(false)}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 0.5 }}
                      >
                        Manage Problems
                      </Button>
                      <Button
                        fullWidth
                        component={Link}
                        to="/admin/problem-feedback"
                        onClick={toggleDrawer(false)}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 0.5 }}
                      >
                        Problem Feedback
                      </Button>
                      <Button
                        fullWidth
                        component={Link}
                        to="/admin/manage-contests"
                        onClick={toggleDrawer(false)}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 0.5 }}
                      >
                        Manage Contests
                      </Button>
                      <Button
                        fullWidth
                        component={Link}
                        to="/admin/manage-neet-tests"
                        onClick={toggleDrawer(false)}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 0.5 }}
                      >
                        Manage NEET Tests
                      </Button>
                      <Button
                        fullWidth
                        component={Link}
                        to="/admin/manage-users"
                        onClick={toggleDrawer(false)}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 0.5 }}
                      >
                        Manage Users
                      </Button>
                      <Button
                        fullWidth
                        component={Link}
                        to="/admin/manage-careers"
                        onClick={toggleDrawer(false)}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 0.5 }}
                      >
                        Career Applications
                      </Button>
                      <Button
                        fullWidth
                        component={Link}
                        to="/admin/broadcast-email"
                        onClick={toggleDrawer(false)}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 0.5 }}
                      >
                        Broadcast Email
                      </Button>
                      <Button
                        fullWidth
                        component={Link}
                        to="/admin/analytics"
                        onClick={toggleDrawer(false)}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 0.5 }}
                      >
                        Analytics
                      </Button>
                    </>
                  )}
                  {loggedInUser?.role === 'softadmin' && (
                    <>
                      <Button
                        fullWidth
                        component={Link}
                        to="/admin/manage-problems"
                        onClick={toggleDrawer(false)}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 0.5 }}
                      >
                        Manage Problems
                      </Button>
                      <Button
                        fullWidth
                        component={Link}
                        to="/admin/problem-feedback"
                        onClick={toggleDrawer(false)}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 0.5 }}
                      >
                        Problem Feedback
                      </Button>
                    </>
                  )}
                </>
              )}
              <Button
                fullWidth
                component={Link}
                to="/profile"
                onClick={toggleDrawer(false)}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 0.5 }}
              >
                My Profile
              </Button>
              <Button
                fullWidth
                component={Link}
                to="/saved"
                onClick={toggleDrawer(false)}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 0.5 }}
              >
                Saved Problems
              </Button>
              <Button
                fullWidth
                startIcon={<AutoAwesomeOutlinedIcon />}
                onClick={() => {
                  toggleDrawer(false)();
                  window.dispatchEvent(new CustomEvent(TOUR_START_EVENT));
                }}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 0.5 }}
              >
                Take a quick tour
              </Button>
              <Button
                fullWidth
                onClick={() => {
                  toggleDrawer(false)();
                  handleLogout();
                }}
                sx={{
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  color: ACCENT_PINK,
                  fontWeight: 700,
                }}
              >
                Log out
              </Button>
            </>
          ) : (
            <>
              <Button
                fullWidth
                startIcon={<AutoAwesomeOutlinedIcon />}
                onClick={() => {
                  toggleDrawer(false)();
                  window.dispatchEvent(new CustomEvent(TOUR_START_EVENT));
                }}
                sx={{ justifyContent: 'flex-start', textTransform: 'none', mb: 1 }}
              >
                Take a quick tour
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  toggleDrawer(false)();
                  setAuthModalOpen(true);
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  bgcolor: ACCENT,
                  '&:hover': { bgcolor: '#0f5c5e' },
                }}
              >
                Sign in
              </Button>
            </>
          )}
        </Box>
      </Drawer>

      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} />
    </>
  );
};

export default Navbar;
